import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Grid,
  TextField,
  Button,
  Chip,
  Divider,
  Stack,
  CircularProgress,
  Alert,
  Rating,
} from "@mui/material";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
  ResolveTenancyCondition,
  UploadMoveInPhotos,
  SubmitDamageClaim,
  FinalizeDamageClaim,
  conditionBody,
} from "../redux/reducer/PropertyCondition";
import {
  GetLandlordSettlementBills,
  VerifySettlementReceipt,
  settlementBody,
} from "../redux/reducer/SettlementBill";
import {
  GetReviewForTenancy,
  RespondToReview,
  reviewBody,
} from "../redux/reducer/PropertyReview";

const HEADER_GRADIENT = "linear-gradient(135deg, #0f766e 0%, #0891b2 100%)";
const MAX_PHOTOS = 5;

const money = (n) => `Rs ${Number(n || 0).toLocaleString("en-US")}`;

const PhotoGrid = ({ photos, empty }) => {
  if (!photos?.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        {empty}
      </Typography>
    );
  }
  return (
    <Grid container spacing={1}>
      {photos.map((p, i) => (
        <Grid item xs={4} key={p.publicId || i}>
          <Box
            component="a"
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: "block" }}
          >
            <Box
              component="img"
              src={p.url}
              alt={`Photo ${i + 1}`}
              sx={{ width: "100%", height: 80, objectFit: "cover", borderRadius: 1.5 }}
            />
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

export default function PropertyConditionDialog({
  open,
  onClose,
  tenantId,
  flatId,
  tenantName,
  flatName,
  landlordId,
}) {
  const dispatch = useDispatch();
  const { condition, tenancyRequestId, loading } = useSelector(
    (state) => state.propertyCondition
  );
  const { bills } = useSelector((state) => state.settlementBill);
  const { forTenancy: review } = useSelector((state) => state.propertyReview);

  const [moveInPhotos, setMoveInPhotos] = useState([]);
  const [savingMoveIn, setSavingMoveIn] = useState(false);

  const [description, setDescription] = useState("");
  const [claimedAmount, setClaimedAmount] = useState("");
  const [claimPhotos, setClaimPhotos] = useState([]);
  const [filingClaim, setFilingClaim] = useState(false);

  const [finalAmount, setFinalAmount] = useState("");
  const [decisionNote, setDecisionNote] = useState("");
  const [finalizing, setFinalizing] = useState(false);

  const [verifyingId, setVerifyingId] = useState(null);

  const [ownerResponseMsg, setOwnerResponseMsg] = useState("");
  const [respondingToReview, setRespondingToReview] = useState(false);

  useEffect(() => {
    if (open && tenantId && flatId) {
      dispatch(ResolveTenancyCondition({ tenantId, flatId }));
      if (landlordId) dispatch(GetLandlordSettlementBills({ landlordId }));
    }
  }, [open, tenantId, flatId, landlordId, dispatch]);

  useEffect(() => {
    if (tenancyRequestId) dispatch(GetReviewForTenancy({ tenancyRequestId }));
  }, [tenancyRequestId, dispatch]);

  useEffect(() => {
    if (condition?.claimedAmount) setFinalAmount(String(condition.claimedAmount));
  }, [condition?.claimedAmount]);

  const bill = bills.find((b) => b._id === condition?.settlementBillId);

  const handleUploadMoveIn = async () => {
    if (!moveInPhotos.length) {
      toast.warning("Please select at least one photo.");
      return;
    }
    setSavingMoveIn(true);
    try {
      const res = await dispatch(
        UploadMoveInPhotos({ tenancyRequestId, photos: moveInPhotos })
      );
      const body = conditionBody(res);
      if (body?.success) {
        toast.success("Move-in photos saved.");
        setMoveInPhotos([]);
      } else {
        toast.error(body?.message || "Failed to save photos");
      }
    } finally {
      setSavingMoveIn(false);
    }
  };

  const handleFileClaim = async () => {
    if (!description.trim() || !claimedAmount || Number(claimedAmount) <= 0) {
      toast.warning("Please describe the damage and enter an amount.");
      return;
    }
    if (!claimPhotos.length) {
      toast.warning("Please attach at least one move-out photo.");
      return;
    }
    setFilingClaim(true);
    try {
      const res = await dispatch(
        SubmitDamageClaim({
          tenancyRequestId,
          description,
          claimedAmount,
          photos: claimPhotos,
        })
      );
      const body = conditionBody(res);
      if (body?.success) {
        toast.success("Damage claim filed. The tenant has 7 days to respond.");
        setDescription("");
        setClaimedAmount("");
        setClaimPhotos([]);
      } else {
        toast.error(body?.message || "Failed to file claim");
      }
    } finally {
      setFilingClaim(false);
    }
  };

  const handleFinalize = async () => {
    setFinalizing(true);
    try {
      const res = await dispatch(
        FinalizeDamageClaim({ id: condition._id, finalAmount, note: decisionNote })
      );
      const body = conditionBody(res);
      if (body?.success) {
        toast.success("Damage claim finalized.");
        setDecisionNote("");
      } else {
        toast.error(body?.message || "Failed to finalize");
      }
    } finally {
      setFinalizing(false);
    }
  };

  const handleVerify = async (decision) => {
    setVerifyingId(bill._id);
    try {
      const res = await dispatch(
        VerifySettlementReceipt({ id: bill._id, decision, reason: "" })
      );
      const body = settlementBody(res);
      if (body?.success) {
        toast.success(decision === "VERIFY" ? "Payment verified." : "Receipt rejected.");
      } else {
        toast.error(body?.message || "Failed to record decision");
      }
    } finally {
      setVerifyingId(null);
    }
  };

  const handleRespondToReview = async () => {
    if (!ownerResponseMsg.trim()) {
      toast.warning("Please write a response.");
      return;
    }
    setRespondingToReview(true);
    try {
      const res = await dispatch(
        RespondToReview({ id: review._id, message: ownerResponseMsg })
      );
      const body = reviewBody(res);
      if (body?.success) {
        toast.success("Response saved.");
        setOwnerResponseMsg("");
      } else {
        toast.error(body?.message || "Failed to save response");
      }
    } finally {
      setRespondingToReview(false);
    }
  };

  const canFileClaim = !condition || ["NONE", "RESOLVED", "EXPIRED"].includes(condition.status);
  const claimOpen = condition && ["CLAIMED", "DISPUTED"].includes(condition.status);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            background: HEADER_GRADIENT,
            color: "#fff",
            p: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <HomeIcon sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Property Condition
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {tenantName ? `${tenantName} — ` : ""}
                {flatName || "This property"}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Move-in photos (before)
            </Typography>
            <PhotoGrid
              photos={condition?.moveInPhotos}
              empty="No move-in photos saved yet."
            />
            <Box sx={{ mt: 1.5 }}>
              <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={<AttachFileIcon fontSize="small" />}
              >
                {moveInPhotos.length > 0
                  ? `${moveInPhotos.length} selected`
                  : "Select photos"}
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  onChange={(e) =>
                    setMoveInPhotos(Array.from(e.target.files || []).slice(0, MAX_PHOTOS))
                  }
                />
              </Button>
              <Button
                size="small"
                variant="contained"
                sx={{ ml: 1 }}
                disabled={savingMoveIn || !moveInPhotos.length}
                onClick={handleUploadMoveIn}
              >
                {savingMoveIn ? "Saving…" : "Save"}
              </Button>
            </Box>

            <Divider sx={{ my: 2.5 }} />

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Move-out photos (after) &amp; damage claim
            </Typography>
            <PhotoGrid
              photos={condition?.moveOutPhotos}
              empty="No move-out photos on record."
            />

            {condition?.status && condition.status !== "NONE" && (
              <Box sx={{ mt: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={condition.status} color="warning" />
                  <Typography variant="body2" fontWeight={700}>
                    Claimed: {money(condition.claimedAmount)}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {condition.damageDescription}
                </Typography>
                {condition.respondByDate && condition.status === "CLAIMED" && (
                  <Typography variant="caption" color="text.secondary">
                    Tenant can respond until{" "}
                    {dayjs(condition.respondByDate).format("DD MMM YYYY")}.
                  </Typography>
                )}
                {condition.tenantResponse?.message && (
                  <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
                    Tenant's response: "{condition.tenantResponse.message}"
                  </Alert>
                )}
                {["RESOLVED", "EXPIRED"].includes(condition.status) && (
                  <Alert severity="success" variant="outlined" sx={{ mt: 1.5 }}>
                    Finalized at {money(condition.finalAmount)}
                    {condition.decidedBy === "auto"
                      ? " (tenant did not respond in time)."
                      : "."}
                  </Alert>
                )}
              </Box>
            )}

            {claimOpen && (
              <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: "#f8fafc" }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  Finalize this claim
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Final amount"
                  value={finalAmount}
                  onChange={(e) => setFinalAmount(e.target.value)}
                  sx={{ mt: 1, mb: 1, bgcolor: "#fff" }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Note (optional)"
                  value={decisionNote}
                  onChange={(e) => setDecisionNote(e.target.value)}
                  sx={{ mb: 1, bgcolor: "#fff" }}
                />
                <Button
                  size="small"
                  variant="contained"
                  disabled={finalizing}
                  onClick={handleFinalize}
                >
                  {finalizing ? "Finalizing…" : "Finalize amount"}
                </Button>
              </Box>
            )}

            {canFileClaim && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  label="Describe the damage"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Claimed amount (Rs)"
                  value={claimedAmount}
                  onChange={(e) => setClaimedAmount(e.target.value)}
                  sx={{ mb: 1.5 }}
                />
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<AttachFileIcon fontSize="small" />}
                >
                  {claimPhotos.length > 0
                    ? `${claimPhotos.length} selected`
                    : "Attach move-out photos"}
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={(e) =>
                      setClaimPhotos(Array.from(e.target.files || []).slice(0, MAX_PHOTOS))
                    }
                  />
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  sx={{ ml: 1 }}
                  disabled={filingClaim}
                  onClick={handleFileClaim}
                >
                  {filingClaim ? "Filing…" : "File damage claim"}
                </Button>
              </Box>
            )}

            {bill && (
              <>
                <Divider sx={{ my: 2.5 }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Final settlement bill
                </Typography>
                <Typography variant="body2">
                  Amount owed beyond deposit: <strong>{money(bill.amount)}</strong>
                </Typography>
                <Chip size="small" label={bill.status} sx={{ mt: 1 }} />
                {bill.status === "SUBMITTED" && bill.receipt?.url && (
                  <Box sx={{ mt: 1.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      href={bill.receipt.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ mb: 1 }}
                    >
                      View receipt
                    </Button>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={Boolean(verifyingId)}
                        onClick={() => handleVerify("VERIFY")}
                      >
                        Verify payment
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        disabled={Boolean(verifyingId)}
                        onClick={() => handleVerify("REJECT")}
                      >
                        Reject
                      </Button>
                    </Stack>
                  </Box>
                )}
              </>
            )}

            {review && (
              <>
                <Divider sx={{ my: 2.5 }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Tenant's review
                </Typography>
                <Rating value={review.rating} readOnly size="small" />
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {review.comment || <em>No comment</em>}
                </Typography>
                <Chip
                  size="small"
                  sx={{ mt: 1 }}
                  label={
                    review.status === "APPROVED"
                      ? "Published"
                      : review.status === "REJECTED"
                      ? "Not approved"
                      : "Awaiting admin review"
                  }
                />

                {review.ownerResponse?.message ? (
                  <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
                    Your response: "{review.ownerResponse.message}"
                  </Alert>
                ) : (
                  <Box sx={{ mt: 1.5 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      placeholder="Respond to this review (shown alongside it once published)…"
                      value={ownerResponseMsg}
                      onChange={(e) => setOwnerResponseMsg(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      disabled={respondingToReview}
                      onClick={handleRespondToReview}
                    >
                      {respondingToReview ? "Saving…" : "Save response"}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
